import s from "./PersonalDataManagement.less";
import { Input } from "~/shared/ui/Fields/Input";
import Button from "~/shared/ui/Fields/Button";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGetLocationData } from "~/hooks/locationData";
import { OpenModalActions } from "~/types/OpenModalActions";
import { SubmitHandler, useForm } from "react-hook-form/dist/index.ie11";
import { getFields } from "./fields";
import { personalDataQueryManagement } from "./queryManagement";
import Select from "~/shared/ui/Fields/Select";
import { useGetAllInfoSystemsForSelect } from "~/hooks/Selectors/useGetAllInfoSystemsForSelect";
import { SelectOptions } from "~/types/Select";
import { useGetPersonalInformationByIdQuery } from "~/schemas/generated/graphql";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { getSchema } from "./fieldsValidity";
import FilePickerField from "../FilePickerField";

type FormSubmit = {
  name: string;
  file: string;
  infoSystem: SelectOptions;
};

const PersonalDataManagement = ({ refetch, closeModal }) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useGetLocationData();

  const isAdd = openModal === OpenModalActions.Add;
  const isEdit = openModal === OpenModalActions.Edit;
  const isView = openModal === OpenModalActions.View;

  const readyToGetPersonalDataById =
    Boolean(isEdit && currentId) || Boolean(isView && currentId);

  const { data, loading } = useGetPersonalInformationByIdQuery({
    variables: {
      id: currentId,
    },
    skip: !readyToGetPersonalDataById,
    fetchPolicy: "cache-and-network",
  });

  const personalDataItem = data?.PositionGetter?.getPersonalInformationById;

  const currentFileToFilePicker = {
    id: personalDataItem?.File.id,
    link: personalDataItem?.File.link,
    name: personalDataItem?.File.name,
  };

  const defaultValues = {
    name: personalDataItem?.name || "",
    file: personalDataItem?.File.id || null,
    infoSystem: {
      label: personalDataItem?.InformationSystem?.name,
      value: personalDataItem?.InformationSystem?.id,
    } || { label: "Не выбрано", value: undefined },
  };

  const { handleSubmit, control, errors, setValue, setError, reset } = useForm({
    defaultValues,
    resolver: yupResolver(getSchema()),
  });

  const { infoSystems, intersectionObserver, searchText } =
    useGetAllInfoSystemsForSelect();

  const fields = getFields({
    errors,
    infoSystems,
    intersectionObserver,
    searchText,
  });

  const { editPersonalData, addPersonalData } = personalDataQueryManagement({
    refetch,
    closeModal,
  });

  const submit: SubmitHandler<FormSubmit> = (data) => {
    const { name, infoSystem, file } = data;
    if (isAdd) {
      addPersonalData({
        variables: {
          name,
          informationSystemId: infoSystem.value,
          personalDataTemplateId: file,
        },
      });
    }
    if (isEdit) {
      editPersonalData({
        variables: {
          id: currentId,
          name,
          personalDataTemplate: file,
          informationSystemId: infoSystem.value,
        },
      });
    }
  };

  useEffect(() => {
    reset(defaultValues);
  }, [personalDataItem, loading]);

  return (
    <form className={s.form} onSubmit={handleSubmit(submit)}>
      <div className={s.items}>
        {fields.map((field) => (
          <div className={s.item} key={field.name}>
            {field.isInput && (
              <Input
                label={field.label}
                name={field.name}
                control={control}
                customClassName={s.input}
                error={field.error}
                disabled={field.disabled}
              />
            )}
            {field.isSelect && (
              <Select
                name={field.name}
                label={field.label}
                control={control}
                optionObjects={field.options}
                getSearchText={field.searchText}
                onScrollBottom={field.scrollBottom}
                error={field.error}
                disabled={field.disabled}
                customClassName={s.select}
              />
            )}
            {field.isFilePicker && !isView && (
              <FilePickerField
                label={field.label}
                control={control}
                name={field.name}
                error={field.error}
                errors={field.errors}
                setError={setError}
                DataConsent={currentFileToFilePicker}
                setValue={setValue}
                id={field.name}
              />
            )}
            {field.isFilePicker && isView && (
              <div className={s.custom_file_wrapper}>
                <span className={s.custom_label}>{field.label}</span>
                <a
                  href={currentFileToFilePicker.link}
                  className={s.custom_file}
                >
                  {currentFileToFilePicker.name}
                </a>
              </div>
            )}
          </div>
        ))}
        <div className={s.item}>
          {!isView && <Button txtBtn={t("ui.save")} isForm />}
        </div>
      </div>
    </form>
  );
};

export default PersonalDataManagement;
